import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import {
  Plus, Edit2, Trash2, Save, X, ArrowUp, ArrowDown, Sparkles, Lock,
  ExternalLink, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import Brand from "../../components/Brand";

/* /dashboard/affiliate-manager (admin-only)
   CRUD for the AFFILIRAMA programs. Same pattern as BookManagerTab.
*/

const EMPTY_PROGRAM = {
  name: "",
  tagline: "",
  category: "Wellness",
  image_url: "",
  affiliate_url: "",
  commission_text: "",
  description: "",
  sort_order: 0,
  badge: "",
  badge_color: "#22693C",
  est_monthly_income: "",
  active: true,
};

const CATEGORIES = ["Wellness", "Fitness", "Nutrition", "Education", "Other"];
const BADGE_PRESETS = [
  { label: "FAITH-BACKED", color: "#0F172A" },
  { label: "EASY SELL",    color: "#22693C" },
  { label: "GIFT-WORTHY",  color: "#F39A1F" },
  { label: "BEST PAYOUT",  color: "#287F8E" },
  { label: "HOT NEW",      color: "#2E9F3A" },
  { label: "STARTER",      color: "#287F8E" },
  { label: "PRO",          color: "#2E9F3A" },
];

export default function AffiliateManagerTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/affiliate-programs");
      setItems(r.data.programs || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load programs");
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...EMPTY_PROGRAM, sort_order: (items[items.length - 1]?.sort_order || 0) + 1 });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      if (editing.id) {
        await api.put(`/admin/affiliate-programs/${editing.id}`, editing);
        toast.success("Program updated");
      } else {
        await api.post("/admin/affiliate-programs", editing);
        toast.success("Program added");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/affiliate-programs/${p.id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const move = async (p, dir) => {
    const sorted = [...items].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const idx = sorted.findIndex(x => x.id === p.id);
    const swapWith = dir === "up" ? sorted[idx - 1] : sorted[idx + 1];
    if (!swapWith) return;
    try {
      await api.post(`/admin/affiliate-programs/${p.id}/sort`, { sort_order: swapWith.sort_order });
      await api.post(`/admin/affiliate-programs/${swapWith.id}/sort`, { sort_order: p.sort_order });
      await load();
    } catch {
      toast.error("Reorder failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="affiliate-manager-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-gold-deep">★ <Brand/> ADMIN</div>
          <h2 className="text-3xl uppercase text-red-wine leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700 }}>
            Partners Manager
          </h2>
          <div className="text-sm text-foreground mt-0.5">
            Add affiliate URLs, commissions, and badges. Free members see the cards locked; paid members see the live links.
          </div>
        </div>
        {!editing && (
          <button onClick={startNew} data-testid="affil-add-new" className="inline-flex items-center gap-2 bg-[#F39A1F] text-[#0F172A] px-5 py-3 rounded-md font-bold uppercase tracking-wider-cap text-xs hover:bg-[#FFB54D] shadow">
            <Plus className="w-4 h-4"/> Add New Program
          </button>
        )}
      </div>

      {editing && (
        <div className="rounded-2xl border-2 border-[#F39A1F] bg-white shadow-2xl p-6 space-y-4" data-testid="affil-editor">
          <div className="flex items-center justify-between">
            <h3 className="font-serif-display text-2xl text-red-wine">{editing.id ? "Edit Program" : "New Program"}</h3>
            <button onClick={cancel} className="text-foreground/60 hover:text-red-wine"><X className="w-5 h-5"/></button>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-wider-cap font-bold text-foreground/70 uppercase">Program Name *</label>
              <input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })}
                     data-testid="affil-name"
                     className="w-full mt-1 px-3 py-2.5 rounded-md border-2 border-border focus:border-[#F39A1F] outline-none transition"/>
            </div>
            <div>
              <label className="text-[10px] tracking-wider-cap font-bold text-foreground/70 uppercase">Category</label>
              <select value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })}
                      data-testid="affil-category"
                      className="w-full mt-1 px-3 py-2.5 rounded-md border-2 border-border focus:border-[#F39A1F] outline-none transition">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-foreground/70 uppercase">Tagline (one-liner)</label>
            <input value={editing.tagline || ""} onChange={e => setEditing({ ...editing, tagline: e.target.value })}
                   data-testid="affil-tagline"
                   placeholder="20 min/day · senior-friendly · no equipment"
                   className="w-full mt-1 px-3 py-2.5 rounded-md border-2 border-border focus:border-[#F39A1F] outline-none transition"/>
          </div>

          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-foreground/70 uppercase">Cover Image URL</label>
            <input value={editing.image_url || ""} onChange={e => setEditing({ ...editing, image_url: e.target.value })}
                   data-testid="affil-image"
                   placeholder="https://… (use Pexels free stock if needed)"
                   className="w-full mt-1 px-3 py-2.5 rounded-md border-2 border-border focus:border-[#F39A1F] outline-none transition"/>
            {editing.image_url && (
              <div className="mt-2 aspect-video w-full max-w-sm rounded overflow-hidden border-2 border-border bg-white">
                <img src={editing.image_url} alt="preview" className="w-full h-full object-contain p-2 bg-white"/>
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-foreground/70 uppercase">Affiliate URL (the actual unlocked link)</label>
            <input value={editing.affiliate_url || ""} onChange={e => setEditing({ ...editing, affiliate_url: e.target.value })}
                   data-testid="affil-url"
                   placeholder="https://your-affiliate-link?ref=faitharama"
                   className="w-full mt-1 px-3 py-2.5 rounded-md border-2 border-border focus:border-[#F39A1F] outline-none transition font-mono text-sm"/>
            <div className="text-[10px] text-foreground/60 mt-1 italic">Leave blank to show "Coming Soon" placeholder.</div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-wider-cap font-bold text-foreground/70 uppercase">Commission Text</label>
              <input value={editing.commission_text || ""} onChange={e => setEditing({ ...editing, commission_text: e.target.value })}
                     data-testid="affil-commission"
                     placeholder="$15 per sale · 30% recurring"
                     className="w-full mt-1 px-3 py-2.5 rounded-md border-2 border-border focus:border-[#F39A1F] outline-none transition"/>
            </div>
            <div>
              <label className="text-[10px] tracking-wider-cap font-bold text-foreground/70 uppercase">Est. Monthly Income</label>
              <input value={editing.est_monthly_income || ""} onChange={e => setEditing({ ...editing, est_monthly_income: e.target.value })}
                     data-testid="affil-income"
                     placeholder="$30-150/mo"
                     className="w-full mt-1 px-3 py-2.5 rounded-md border-2 border-border focus:border-[#F39A1F] outline-none transition"/>
            </div>
          </div>

          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-foreground/70 uppercase">Description (1-2 sentences)</label>
            <textarea rows={3} value={editing.description || ""} onChange={e => setEditing({ ...editing, description: e.target.value })}
                      data-testid="affil-description"
                      className="w-full mt-1 px-3 py-2.5 rounded-md border-2 border-border focus:border-[#F39A1F] outline-none transition resize-y"/>
          </div>

          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-foreground/70 uppercase">Badge</label>
            <div className="flex gap-1 flex-wrap mt-1">
              {BADGE_PRESETS.map(p => (
                <button key={p.label} type="button"
                        onClick={() => setEditing({ ...editing, badge: p.label, badge_color: p.color })}
                        className={`text-[10px] px-3 py-1.5 rounded font-bold uppercase tracking-wider-cap transition ${editing.badge === p.label ? "ring-2 ring-[#0F172A]" : ""}`}
                        style={{ background: p.color, color: "white" }}>
                  {p.label}
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-[1fr_120px] gap-2 mt-2">
              <input value={editing.badge || ""} onChange={e => setEditing({ ...editing, badge: e.target.value })}
                     data-testid="affil-badge" placeholder="Custom badge text"
                     className="px-3 py-2 rounded-md border-2 border-border text-xs"/>
              <input type="color" value={editing.badge_color || "#22693C"} onChange={e => setEditing({ ...editing, badge_color: e.target.value })}
                     data-testid="affil-badge-color" className="h-10 rounded-md cursor-pointer"/>
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={editing.active !== false} onChange={e => setEditing({ ...editing, active: e.target.checked })} data-testid="affil-active"/>
            <span><strong>Visible in the Market</strong> — uncheck to hide</span>
          </label>

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button onClick={cancel} className="px-5 py-2.5 rounded-md text-sm font-bold uppercase tracking-wider-cap text-foreground/70 hover:text-red-wine">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="affil-save"
                    className="inline-flex items-center gap-2 bg-red-wine text-white px-6 py-2.5 rounded-md text-sm font-bold uppercase tracking-wider-cap hover:bg-[#1F8A2A] disabled:opacity-60 shadow">
              <Save className="w-4 h-4"/> {busy ? "Saving…" : (editing.id ? "Update Program" : "Save Program")}
            </button>
          </div>
        </div>
      )}

      <div>
        <div className="text-[10px] tracking-wider-cap font-bold text-foreground/60 uppercase mb-3">
          {items.length} Program{items.length === 1 ? "" : "s"}
        </div>
        <div className="space-y-3">
          {items.map((p, i) => (
            <div key={p.id} data-testid={`affil-row-${p.id}`} className="rounded-xl bg-white border-2 border-border hover:border-[#F39A1F] transition shadow-sm flex items-center gap-4 p-3">
              <div className="w-20 h-14 rounded overflow-hidden bg-white flex-shrink-0 border border-border">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-contain p-2 bg-white"/>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-foreground/30"><Sparkles className="w-5 h-5"/></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-serif-display text-lg text-red-wine">{p.name}</div>
                  {p.badge && <span className="text-[9px] tracking-wider-cap uppercase font-bold px-2 py-0.5 rounded text-white" style={{ background: p.badge_color }}>{p.badge}</span>}
                  {p.active === false && <span className="text-[9px] tracking-wider-cap uppercase font-bold px-2 py-0.5 rounded bg-foreground/15 text-foreground/70">Hidden</span>}
                  {!p.affiliate_url && <span className="text-[9px] tracking-wider-cap uppercase font-bold px-2 py-0.5 rounded bg-[#FFE0B2] text-[#7A4F00] inline-flex items-center gap-1"><Lock className="w-2.5 h-2.5"/> No URL Yet</span>}
                </div>
                <div className="text-xs text-foreground/70 truncate">{p.tagline}</div>
                <div className="text-xs text-foreground/60 mt-0.5">
                  <strong className="text-foreground">{p.commission_text || "—"}</strong> · {p.est_monthly_income || "—"} · {p.category}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => move(p, "up")} disabled={i === 0} data-testid={`affil-up-${p.id}`} className="p-2 rounded hover:bg-white disabled:opacity-25"><ArrowUp className="w-4 h-4"/></button>
                <button onClick={() => move(p, "down")} disabled={i === items.length - 1} data-testid={`affil-down-${p.id}`} className="p-2 rounded hover:bg-white disabled:opacity-25"><ArrowDown className="w-4 h-4"/></button>
                {p.affiliate_url && <a href={p.affiliate_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded hover:bg-white text-foreground/60 hover:text-red-wine"><ExternalLink className="w-4 h-4"/></a>}
                <button onClick={() => setEditing({ ...p })} data-testid={`affil-edit-${p.id}`} className="p-2 rounded hover:bg-white text-red-wine"><Edit2 className="w-4 h-4"/></button>
                <button onClick={() => remove(p)} data-testid={`affil-delete-${p.id}`} className="p-2 rounded hover:bg-white text-[#2E9F3A]"><Trash2 className="w-4 h-4"/></button>
              </div>
            </div>
          ))}
          {!items.length && !editing && (
            <div className="rounded-xl border-2 border-dashed border-[#F39A1F]/40 bg-white p-10 text-center">
              <TrendingUp className="w-10 h-10 text-gold-deep mx-auto mb-2"/>
              <div className="font-serif-display text-xl text-red-wine">No programs yet.</div>
              <div className="text-sm text-foreground/70 mt-1">Click <strong>Add New Program</strong> above to create your first one.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
